import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import APIClient from "@/APIClient";
import { QueryKey } from "./query-keys";
import { useToast } from "@/composable/useToast";
import { type Ref } from "vue";
import type { UpdateReportPayload } from "@/types/diagnostics.types";
import type { Section } from "@/types/sections.types";

export const useGetDiagnosticReports = (
  diagnosticId: Ref<number | undefined>,
) => {
  return useQuery({
    queryKey: [QueryKey.REPORTS, { diagnosticId }],
    queryFn: async () => {
      if (!diagnosticId.value) throw new Error("No diagnostic id provided");
      return await APIClient.fetchDiagnosticReports(diagnosticId.value);
    },
    enabled: () => !!diagnosticId.value,
    refetchOnWindowFocus: false,
  });
};

export const useGetReport = (reportId: Ref<number | undefined>) => {
  const queryClient = useQueryClient();
  const queryKey = [QueryKey.REPORTS, reportId];

  return useQuery({
    queryKey,
    queryFn: async () => {
      if (!reportId.value) throw new Error("No report id provided");
      const res = await APIClient.fetchReport(reportId.value);
      if (res.isGeneratingFindings) {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey });
        }, 5000);
      }
      return res;
    },
    enabled: () => !!reportId.value,
    refetchOnWindowFocus: false,
  });
};

export const useCreateReport = (diagnosticId: Ref<number | undefined>) => {
  const queryClient = useQueryClient();
  const { showSuccess, showWarning } = useToast();

  return useMutation({
    mutationFn: async (name?: string) => {
      if (!diagnosticId.value) throw new Error("No diagnostic id provided");
      return await APIClient.createReport(diagnosticId.value, name);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORTS, { diagnosticId: diagnosticId.value }],
      });
      showSuccess("toast:reports.create-success");
    },
    onError: () => {
      showWarning("toast:reports.create-error");
    },
  });
};

export const useUpdateReport = (reportId: Ref<number | undefined>) => {
  const { showSuccess, showLoading } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateReportPayload) => {
      if (!reportId.value) throw new Error("No report id provided");
      const handle = showLoading("toast:reports.saving");
      return await APIClient.updateReport(reportId.value, payload).finally(
        () => {
          handle.close();
        },
      );
    },
    onSuccess: (data) => {
      showSuccess("toast:reports.save-success");
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORTS, reportId],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORTS, { diagnosticId: data.diagnosticId }],
      });
    },
  });
};

/**
 * Silent variant of useUpdateReport: no toasts, no loading indicator.
 * Meant for debounced auto-save on fields like conclusion/preconisation.
 */
export const useUpdateReportSilent = (reportId: Ref<number | undefined>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateReportPayload) => {
      if (!reportId.value) throw new Error("No report id provided");
      return await APIClient.updateReport(reportId.value, payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORTS, reportId],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORTS, { diagnosticId: data.diagnosticId }],
      });
    },
  });
};

export const useDeleteReport = (reportId: Ref<number | undefined>) => {
  const queryClient = useQueryClient();
  const { showSuccess, showWarning } = useToast();

  return useMutation({
    mutationFn: async () => {
      if (!reportId.value) throw new Error("No report id provided");
      return await APIClient.deleteReport(reportId.value);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.REPORTS] });
      showSuccess("toast:reports.delete-success");
    },
    onError: () => {
      showWarning("toast:reports.delete-error");
    },
  });
};

// ==========================================
// IQOA Association
// ==========================================

export const useStartIQOAAssociation = (reportId: Ref<number | undefined>) => {
  const queryClient = useQueryClient();
  const { showWarning } = useToast();

  return useMutation({
    mutationFn: async (payload: {
      catalogueIds: number[];
      replaceExisting: boolean;
    }) => {
      if (!reportId.value) throw new Error("No report id provided");
      return await APIClient.startIQOAAssociation(reportId.value, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORTS, "iqoa-status", reportId.value],
      });
    },
    onError: () => {
      showWarning("toast:reports.iqoa-launch-error");
    },
  });
};

export const useGetIQOAAssociationStatus = (
  reportId: Ref<number | undefined>,
  enabled: Ref<boolean>,
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [QueryKey.REPORTS, "iqoa-status", reportId],
    queryFn: async () => {
      if (!reportId.value) throw new Error("No report id provided");
      return await APIClient.getIQOAAssociationStatus(reportId.value);
    },
    enabled: () => !!reportId.value && enabled.value,
    refetchInterval: (query) => {
      // Poll every 2 seconds while processing
      if (query.state.data?.isProcessing) {
        return 2000;
      }
      // Stop polling when done - invalidate findings
      if (query.state.data && !query.state.data.isProcessing) {
        queryClient.invalidateQueries({
          predicate: (q) => q.queryKey[0] === QueryKey.FINDINGS,
        });
      }
      return false;
    },
    refetchOnWindowFocus: false,
  });
};

// ==========================================
// Conclusions Generation (AI)
// ==========================================

export const useStartConclusionsGeneration = (
  reportId: Ref<number | undefined>,
) => {
  const queryClient = useQueryClient();
  const { showWarning } = useToast();

  return useMutation({
    mutationFn: async () => {
      if (!reportId.value) throw new Error("No report id provided");
      return await APIClient.startConclusionsGeneration(reportId.value);
    },
    onSuccess: () => {
      // Force the status query to refetch so the polling picks up
      // the new isProcessing=true state immediately instead of waiting
      // for a natural polling tick (which wouldn't happen since the
      // refetchInterval only kicks in once isProcessing is true).
      queryClient.invalidateQueries({
        queryKey: [QueryKey.REPORTS, "conclusions-status", reportId.value],
      });
    },
    onError: () => {
      showWarning("toast:reports.conclusions-launch-error");
    },
  });
};

export const useGetConclusionsGenerationStatus = (
  reportId: Ref<number | undefined>,
  enabled: Ref<boolean>,
) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [QueryKey.REPORTS, "conclusions-status", reportId],
    queryFn: async () => {
      if (!reportId.value) throw new Error("No report id provided");
      return await APIClient.getConclusionsGenerationStatus(reportId.value);
    },
    enabled: () => !!reportId.value && enabled.value,
    refetchInterval: (query) => {
      // Poll every 2 seconds while processing
      if (query.state.data?.isProcessing) {
        return 2000;
      }
      // Stop polling when done - invalidate the report so ReportConclusionsStep
      // re-renders with the new conclusion/preconisation content
      if (
        query.state.data &&
        !query.state.data.isProcessing &&
        reportId.value
      ) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.REPORTS, reportId.value],
        });
      }
      return false;
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetReportFindings = (reportId: Ref<number | undefined>) => {
  return useQuery({
    queryKey: [QueryKey.FINDINGS, "report", reportId],
    queryFn: async () => {
      if (!reportId.value) throw new Error("No report id provided");
      return await APIClient.fetchReportFindings(reportId.value);
    },
    enabled: () => !!reportId.value,
    refetchOnWindowFocus: false,
  });
};

export const useGetReportSectionsTree = (reportId: Ref<number | undefined>) => {
  return useQuery({
    queryKey: [QueryKey.SECTIONS, "tree", reportId],
    queryFn: async (): Promise<Section[]> => {
      if (!reportId.value) throw new Error("No report id provided");
      return await APIClient.fetchReportSectionsTree(reportId.value);
    },
    enabled: () => !!reportId.value,
    refetchOnWindowFocus: false,
  });
};
